/**
 * 연대기 열람 UI — 기록의 상호 링크를 실제로 걸어다닐 수 있게 한다 (지시서 3.6).
 *   최근 기록 → 모험가 클릭 → 그 모험가의 전 행적 → 다시 목록
 *   내 단골 → 단골 클릭 → 그 모험가의 전 행적
 *
 * 서버는 구조화 이벤트만 주고, 문장은 여기서 만든다(다국어 렌더의 자리).
 */
import { API_BASE, GRADES } from '@villager/shared';

interface EventRow {
  id: number;
  tick: number;
  etype: string;
  adv_id: number | null;
  player_id: string | null;
  enemy_id: number | null;
  parcel_id: string | null;
  item_tier: number | null;
  payload: Record<string, unknown> | null;
}

interface PatronRow {
  adv_id: number;
  adv_name: string;
  visits: number;
  spent: number;
  top_grade: number;
}

const grade = (g: unknown): string =>
  typeof g === 'number' && g >= 0 && g < GRADES.length ? `${GRADES[g]}급` : '';

/** 구조화 이벤트 → 사람이 읽는 한 줄. (i18n 도입 시 이 함수만 키 기반으로 교체) */
function sentence(e: EventRow): string {
  const p = e.payload ?? {};
  const who = e.adv_id ? `모험가 #${e.adv_id}` : '누군가';
  switch (e.etype) {
    case 'adv_spawn':
      return `${who}이(가) 남쪽 관문으로 들어왔다.`;
    case 'party_formed':
      return p['merged'] ? `${who}이(가) 길 위에서 파티를 결성했다.` : `${who} 일행이 세계에 도착했다.`;
    case 'party_disband':
      return `${who} 일행이 뜻이 갈려 해산했다.`;
    case 'leader_change':
      return `${who}이(가) ${String(p['from'] ?? '')}을(를) 대신해 일행을 이끈다.`;
    case 'trade':
      return `${who}이(가) 필지 ${e.parcel_id}에서 ${e.item_tier ?? ''}단 무기를 ${String(p['price'] ?? '')}금에 샀다.`;
    case 'kill':
      return `${who}이(가) 적을 쓰러뜨렸다.`;
    case 'boss_kill':
      return `⚔ ${who} 일행이 사천왕 <b>${String(p['boss'] ?? '')}</b>을(를) 격파했다!`;
    case 'grade_up':
      return `${who}이(가) <b>${grade(p['grade'])}</b>으로 올라섰다.`;
    case 'death':
      return `🪦 ${who}(${grade(p['grade'])})이(가) ${String(p['killer'] ?? '')}에게 쓰러졌다.`;
    case 'relic_found':
      return `${who}이(가) 묘비 곁에서 유품 무기(${e.item_tier}단)를 발굴했다.`;
    case 'front_advance':
      return `전선이 북쪽으로 전진했다. (row ${String(p['frontRow'] ?? '')})`;
    case 'front_retreat':
      return `전선이 남쪽으로 밀렸다. (row ${String(p['frontRow'] ?? '')})`;
    case 'raid':
      return p['phase'] === 'warn'
        ? '⚠ 마왕군 습격대가 전선을 넘어왔다.'
        : p['phase'] === 'defended'
          ? `망루가 필지 ${e.parcel_id}의 습격을 막아냈다.`
          : `습격대가 필지 ${e.parcel_id}를 덮쳤다.`;
    case 'parcel_buy':
      return `마을사람이 ${e.parcel_id}를 ${p['type'] === 'village' ? '마을' : '던전'} 필지로 매입했다.`;
    case 'build':
      return `필지 ${e.parcel_id}에 시설이 들어섰다.`;
    case 'tax':
      return `왕국이 보유세 ${String(p['tax'] ?? '')}금을 걷었다.`;
    case 'victory':
      return `★ ${who}이(가) 마왕을 쓰러뜨리고 용사가 되었다!`;
    default:
      return `${e.etype}`;
  }
}

export class ChroniclePanel {
  private el: HTMLElement;
  private body: HTMLElement;
  private titleEl: HTMLElement;
  private open = false;
  private token: string | null = null;

  constructor() {
    const el = document.createElement('div');
    el.id = 'chronicle';
    el.innerHTML = `
      <div class="ch-head">
        <span id="ch-title">📜 무명연대기</span>
        <span>
          <button class="ch-tab" data-tab="recent">최근 기록</button>
          <button class="ch-tab" data-tab="patrons">내 단골</button>
          <button class="ch-x">✕</button>
        </span>
      </div>
      <div class="ch-body">불러오는 중…</div>`;
    document.body.appendChild(el);
    this.el = el;
    this.body = el.querySelector('.ch-body')!;
    this.titleEl = el.querySelector('#ch-title')!;
    el.querySelector('.ch-x')!.addEventListener('click', () => this.hide());
    el.querySelectorAll('.ch-tab').forEach((b) =>
      b.addEventListener('click', () => {
        const tab = (b as HTMLElement).dataset['tab'];
        if (tab === 'recent') void this.showRecent();
        else void this.showPatrons();
      }),
    );

    // 열기 버튼 (HUD 옆)
    const btn = document.createElement('div');
    btn.className = 'chip';
    btn.id = 'ch-open';
    btn.style.pointerEvents = 'auto';
    btn.style.cursor = 'pointer';
    btn.innerHTML = '📜 <b>연대기</b>';
    btn.addEventListener('click', () => this.toggle());
    document.getElementById('hud')?.appendChild(btn);
  }

  setToken(t: string | null): void {
    this.token = t;
  }

  toggle(): void {
    if (this.open) this.hide();
    else void this.showRecent();
  }

  hide(): void {
    this.open = false;
    this.el.style.display = 'none';
  }

  private show(): void {
    this.open = true;
    this.el.style.display = 'flex';
  }

  private async get<T>(path: string, auth = false): Promise<T | null> {
    try {
      const headers: Record<string, string> = {};
      if (auth && this.token) headers['authorization'] = `Bearer ${this.token}`;
      const res = await fetch(`${API_BASE.villager}${path}`, { headers });
      if (!res.ok) return null;
      return (await res.json()) as T;
    } catch {
      return null;
    }
  }

  /** 최근 기록 — 세계 전체의 사실들. */
  async showRecent(): Promise<void> {
    this.show();
    this.titleEl.textContent = '📜 무명연대기 — 최근 기록';
    this.body.innerHTML = '불러오는 중…';
    const rows = await this.get<EventRow[]>('/chronicle/events?limit=80');
    if (!rows) {
      this.body.innerHTML = '<div class="ch-empty">기록을 불러오지 못했다.</div>';
      return;
    }
    if (!rows.length) {
      this.body.innerHTML = '<div class="ch-empty">아직 아무 일도 일어나지 않았다.</div>';
      return;
    }
    this.body.innerHTML = '';
    for (const e of rows) {
      const d = document.createElement('div');
      d.className = 'ch-row';
      d.innerHTML = `<span class="ch-tick">${e.tick}</span> ${sentence(e)}`;
      if (e.adv_id) {
        d.classList.add('ch-link');
        d.addEventListener('click', () => void this.showAdventurer(e.adv_id!));
      }
      this.body.appendChild(d);
    }
  }

  /** 한 모험가의 전 행적 — 기록의 상호 링크. */
  async showAdventurer(advId: number): Promise<void> {
    this.show();
    this.titleEl.textContent = `📜 모험가 #${advId}의 행적`;
    this.body.innerHTML = '불러오는 중…';
    const data = await this.get<{ count: number; events: EventRow[] }>(
      `/chronicle/adventurer/${advId}`,
    );
    if (!data || !data.events.length) {
      this.body.innerHTML = '<div class="ch-empty">남은 기록이 없다.</div>';
      return;
    }
    this.body.innerHTML = '';
    const back = document.createElement('div');
    back.className = 'ch-row ch-link';
    back.innerHTML = '← 최근 기록으로';
    back.addEventListener('click', () => void this.showRecent());
    this.body.appendChild(back);
    for (const e of data.events) {
      const d = document.createElement('div');
      d.className = 'ch-row';
      d.innerHTML = `<span class="ch-tick">${e.tick}</span> ${sentence(e)}`;
      this.body.appendChild(d);
    }
  }

  /** 내 단골 — 이 게임의 핵심 감정 단위. */
  async showPatrons(): Promise<void> {
    this.show();
    this.titleEl.textContent = '📜 내 단골';
    this.body.innerHTML = '불러오는 중…';
    if (!this.token) {
      this.body.innerHTML = '<div class="ch-empty">세션이 없다.</div>';
      return;
    }
    const rows = await this.get<PatronRow[]>('/chronicle/me/patrons?limit=30', true);
    if (!rows || !rows.length) {
      this.body.innerHTML =
        '<div class="ch-empty">아직 단골이 없다. 시설을 지으면 모험가들이 찾아온다.</div>';
      return;
    }
    this.body.innerHTML = '';
    for (const p of rows) {
      const d = document.createElement('div');
      d.className = 'ch-row ch-link';
      d.innerHTML = `<b>${p.adv_name || `#${p.adv_id}`}</b> <span style="opacity:.7">${grade(p.top_grade)}</span> — ${p.visits}회 방문 · ${p.spent}금`;
      d.addEventListener('click', () => void this.showAdventurer(p.adv_id));
      this.body.appendChild(d);
    }
  }
}
